/**
 * LE CATALOGUE DE « MA SEMAINE » : les projets, leurs clients et leurs
 * taches, lus CHACUN A PART. Un module pur (ni React ni SDK) : les lectures
 * lui sont passees, il les lance ensemble et rend ce qui a reussi, plus une
 * note par lecture qui compte et a echoue.
 *
 * Pourquoi chaque lecture est independante et typee : les regles refusent une
 * requete sans `type` a un Membre dont les droits sur une famille sont donnes
 * par type, et un refus ne doit jamais vider les autres listes.
 */

/** Ce que le catalogue lit d'un projet : le client auquel il se rattache. */
export interface ProjetLisible {
  contactId?: string | null;
}

/** Ce que le catalogue lit d'un contact : de quoi le retrouver. */
export interface ContactLisible {
  id: string;
}

/** Les quatre lectures : chacune rend une promesse, refusee ou non. */
export interface LecteursCatalogue<P extends ProjetLisible, C extends ContactLisible, T> {
  projets: () => Promise<readonly P[]>;
  /** Les contacts se lisent PAR TYPE : une lecture sans `type` est refusee. */
  contactsPersonnes: () => Promise<readonly C[]>;
  contactsOrganisations: () => Promise<readonly C[]>;
  taches: () => Promise<readonly T[]>;
}

export interface CatalogueLu<P, C, T> {
  projets: readonly P[];
  /** Les contacts des types lus, fusionnes, sans doublon d'id. */
  contacts: readonly C[];
  taches: readonly T[];
  /** Une note par lecture refusee qui compte, dans l'ordre projets, taches, clients. */
  notes: readonly string[];
}

async function lire<V>(lecteur: () => Promise<readonly V[]>): Promise<readonly V[]> {
  return lecteur();
}

/**
 * Lance les quatre lectures en parallele (`allSettled`, jamais un `all` qui
 * tombe en bloc). `raison` dit un echec en clair.
 */
export async function lireCatalogue<P extends ProjetLisible, C extends ContactLisible, T>(
  lecteurs: LecteursCatalogue<P, C, T>,
  raison: (echec: unknown) => string,
): Promise<CatalogueLu<P, C, T>> {
  const [projets, personnes, organisations, taches] = await Promise.allSettled([
    lire(lecteurs.projets),
    lire(lecteurs.contactsPersonnes),
    lire(lecteurs.contactsOrganisations),
    lire(lecteurs.taches),
  ]);
  const notes: string[] = [];

  const listeProjets = projets.status === 'fulfilled' ? projets.value : [];
  if (projets.status === 'rejected') notes.push(`Projets illisibles : ${raison(projets.reason)}`);

  const listeTaches = taches.status === 'fulfilled' ? taches.value : [];
  if (taches.status === 'rejected') notes.push(`Tâches illisibles : ${raison(taches.reason)}`);

  const contacts = new Map<string, C>();
  const refus: string[] = [];
  for (const lecture of [personnes, organisations]) {
    if (lecture.status === 'rejected') {
      const dite = raison(lecture.reason);
      if (!refus.includes(dite)) refus.push(dite);
      continue;
    }
    for (const contact of lecture.value) if (!contacts.has(contact.id)) contacts.set(contact.id, contact);
  }
  /* Un type refuse ne se signale que s'il laisse un client sans nom : un
     Membre reglé sur un seul type n'a pas a voir une fausse alerte. */
  const clientInconnu = listeProjets.some(
    (projet) => projet.contactId != null && !contacts.has(projet.contactId),
  );
  if (refus.length > 0 && clientInconnu) notes.push(`Clients illisibles : ${refus.join(' ; ')}`);

  return { projets: listeProjets, contacts: [...contacts.values()], taches: listeTaches, notes };
}
